import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, History, Clock, User, FileText } from 'lucide-react';
import { getTemplateChanges } from '../../../services/documentTemplateService';
import logger from '../../../utils/logger';

const DocumentTemplateHistory = ({ template, onClose }) => {
  const [changes, setChanges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchChanges();
  }, [template]);

  const fetchChanges = async () => {
    setIsLoading(true);
    try {
      const data = await getTemplateChanges(template.id);
      setChanges(data);
    } catch (error) {
      logger.error('Error loading template changes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeLabel = (type) => {
    const types = {
      creation: 'Creación',
      revision: 'Revisión',
      approval: 'Aprobación',
      obsolescence: 'Obsolescencia'
    };
    return types[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <History className="w-7 h-7" />
                Historial de Cambios
              </h2>
              <p className="text-purple-100 mt-1">
                {template.name} ({template.code})
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Cargando historial...</p>
            </div>
          ) : changes.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay cambios registrados</p>
            </div>
          ) : (
            <div className="space-y-4">
              {changes.map((change, index) => (
                <motion.div
                  key={change.id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  {/* Header del cambio */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          <FileText className="w-3 h-3" />
                          {getChangeTypeLabel(change.change_type || change.changeType)}
                        </span>
                        {change.previous_version && change.new_version && (
                          <span className="text-sm text-gray-600">
                            v{change.previous_version} → v{change.new_version}
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900">
                        {change.change_description || change.changeDescription || 'Sin descripción'}
                      </h4>
                    </div>
                  </div>

                  {/* Detalles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {(change.change_reason || change.changeReason) && (
                      <div>
                        <span className="text-gray-500">Razón:</span>
                        <p className="text-gray-700">{change.change_reason || change.changeReason}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4" />
                      <span>
                        {change.changed_by?.name ||
                         change.changedBy?.name ||
                         'Usuario no especificado'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>
                        {formatDate(change.change_date || change.changeDate)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default DocumentTemplateHistory;
